/**
 * TypewriterHint 组件
 * 根据评估阶段显示动态打字机效果的提示文字
 * 字幕式布局：文字占据实际空间，影响其他元素布局
 * @see Requirements 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 3.2
 */
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, useAnimate, stagger } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * 评估阶段枚举
 */
export type AssessmentStage =
  | "initial" // 初始状态，等待上传
  | "prescreening" // 图像预检中
  | "prescreenFailed" // 图像预检失败
  | "uploading" // 已选择图片，等待提交
  | "submitting" // 正在提交
  | "processing" // AI 正在分析
  | "loading" // 正在加载历史记录
  | "completed" // 评估完成
  | "failed" // 评估失败
  | "carePlan"; // 护理时间轴

/**
 * TypewriterHint 组件 Props
 */
export interface TypewriterHintProps {
  /** 当前评估阶段 */
  stage: AssessmentStage;
  /** 自定义类名 */
  className?: string;
  /** 是否准备好启动动画（等待其他组件加载完成） */
  ready?: boolean;
}

/**
 * 提示文字结构
 */
interface HintText {
  title: string;
  subtitle: string;
}

/**
 * 获取阶段对应的提示文字
 * @param stage - 当前评估阶段
 * @param t - i18n 翻译函数
 * @returns 对应阶段的提示文字（title + subtitle）
 */
export function getHintText(
  stage: AssessmentStage,
  t: (key: string) => string,
): HintText {
  return {
    title: t(`hints.${stage}.title`),
    subtitle: t(`hints.${stage}.subtitle`),
  };
}

/**
 * 内联打字机效果组件
 * 文字占据实际空间，像字幕一样影响布局
 */
const InlineTypewriter: React.FC<{
  text: string;
  duration?: number;
  className?: string;
}> = ({ text, duration = 0.3, className }) => {
  const [scope, animate] = useAnimate();
  const chars = useMemo(() => text.split(""), [text]);

  useEffect(() => {
    animate("span.char", { opacity: 1 }, { duration, delay: stagger(0.03) });
  }, [animate, duration, text]);

  return (
    <motion.span ref={scope} className={cn("inline", className)}>
      {chars.map((char, idx) => (
        <span key={`${char}-${idx}`} className="char opacity-0">
          {char}
        </span>
      ))}
    </motion.span>
  );
};

/**
 * TypewriterHint 组件
 * 根据评估阶段显示动态打字机效果的提示文字
 * 字幕式布局：占据实际空间，影响其他元素
 */
const TypewriterHint: React.FC<TypewriterHintProps> = ({
  stage,
  className,
  ready = true,
}) => {
  const { t } = useTranslation("assessments");
  const [key, setKey] = useState(0);

  // 当阶段变化时，重置动画
  useEffect(() => {
    if (ready) {
      setKey((prev) => prev + 1);
    }
  }, [stage, ready]);

  // 使用类型断言避免 TypeScript 深度递归问题
  const translate = t as (key: string) => string;
  const hintText = getHintText(stage, translate);

  // 未准备好时显示占位符保持布局稳定
  if (!ready) {
    return (
      <div
        className={cn(
          "w-full text-center py-3 sm:py-4 space-y-0.5 sm:space-y-1",
          className,
        )}
      >
        <div className="text-xl sm:text-3xl font-semibold">
          <span className="invisible">{hintText.title}</span>
        </div>
        <div className="text-sm text-muted-foreground mt-2">
          <span className="invisible">{hintText.subtitle}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "w-full text-center py-3 sm:py-4 space-y-0.5 sm:space-y-1",
        className,
      )}
    >
      {/* 标题 */}
      <div className="text-xl sm:text-3xl font-semibold text-foreground">
        <InlineTypewriter
          key={`title-${key}`}
          text={hintText.title}
          duration={0.25}
        />
      </div>
      {/* 副标题 */}
      <div className="text-sm text-muted-foreground mt-2">
        <InlineTypewriter
          key={`subtitle-${key}`}
          text={hintText.subtitle}
          duration={0.2}
        />
      </div>
    </div>
  );
};

export default TypewriterHint;
